import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    useSidebar,
} from 'shared-ui';
import { LifeBuoy, LogOut } from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { UserInfo } from '@/components/app/UserInfo.tsx';
import { SidebarThemeToggle } from '@/components/app/SidebarThemeToggle.tsx';
import { OrbitSidebarMenuButton } from '@/components/app/OrbitSidebarMenuButton.tsx';
import { OrbitSidebarTrigger } from '@/components/app/OrbitSidebarTrigger.tsx';
import orbitLogoFullBlack from '@/assets/orbit-logo-full-black.png';
import orbitLogoFullWhite from '@/assets/orbit-logo-full-white.png';
import orbitLogoIconBlack from '@/assets/orbit-logo-icon-black.png';
import orbitLogoIconWhite from '@/assets/orbit-logo-icon-white.png';
import type { ProductKey } from '@/components/upsell/upsellContent';

interface AppSidebarProps {
    onSignOutClick: () => void;
    onHelpClick: () => void;
    /**
     * Whether the active client has each product's module. When false, the menu item does not
     * navigate; it calls `onUpsell` so an overlay dialog can be shown without leaving the current
     * module. Defaults to enabled so the sidebar still navigates when no access info is supplied.
     */
    findAndFixEnabled?: boolean;
    saviEnabled?: boolean;
    /** Called with the product key when a disabled product is clicked (no navigation). */
    onUpsell?: (product: ProductKey) => void;
    /** Display name from the authenticated account */
    userName?: string;
    /** Email / UPN from the authenticated account */
    userEmail?: string;
    /** Optional avatar URL */
    userAvatarUrl?: string;
}

export function AppSidebar({
    onSignOutClick,
    onHelpClick,
    findAndFixEnabled = true,
    saviEnabled = true,
    onUpsell,
    userName,
    userEmail,
    userAvatarUrl,
}: AppSidebarProps) {
    const { pathname } = useLocation();
    const { state, isMobile } = useSidebar();
    // The mobile sidebar renders as a full-width Sheet that always shows expanded
    // content; `state` only reflects the persisted desktop open/closed cookie, so
    // ignore it on mobile to keep the full logo left-aligned.
    const isCollapsed = !isMobile && state === 'collapsed';

    const isFindAndFixActive = pathname.startsWith('/find-and-fix');
    const isSaviActive = pathname.startsWith('/savi');

    // Enabled products navigate via <Link>; disabled ones open the upsell overlay without
    // changing the route (mirrors the state-driven Help / Sign Out items below).
    const findAndFixButtonProps = findAndFixEnabled
        ? { render: <Link to="/find-and-fix" /> }
        : {
              onClick: () => {
                  onUpsell?.('find-and-fix');
              },
          };
    const saviButtonProps = saviEnabled
        ? { render: <Link to="/savi" /> }
        : {
              onClick: () => {
                  onUpsell?.('savi');
              },
          };

    const lightLogo = isCollapsed ? orbitLogoIconBlack : orbitLogoFullBlack;
    const darkLogo = isCollapsed ? orbitLogoIconWhite : orbitLogoFullWhite;
    const sizeClass = isCollapsed ? 'h-12 w-12' : 'w-[142px]';

    return (
        <Sidebar collapsible="icon">
            {/* On mobile the Sidebar renders as a Sheet (base-ui Dialog.Popup, position:fixed).
                That fixed element is the containing block for absolute children, so
                right-10 + translate-x-1/2 centres the trigger on the panel's actual right
                edge regardless of responsive width — no viewport maths required. */}
            {isMobile && (
                <div className="bg-background absolute top-2 -right-10 z-[101] flex size-16 translate-x-1/2 items-center justify-center rounded-lg border">
                    <OrbitSidebarTrigger />
                </div>
            )}
            <SidebarHeader className="shrink-0 p-0">
                <header className={`flex h-20 items-center border-b px-2 ${isCollapsed ? 'justify-center' : ''}`}>
                    <img src={lightLogo} alt="Orbit" className={`block object-contain dark:hidden ${sizeClass}`} />
                    <img src={darkLogo} alt="Orbit" className={`hidden object-contain dark:block ${sizeClass}`} />
                </header>
                <div className="hidden p-2 px-4 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0 md:block">
                    <OrbitSidebarTrigger />
                </div>
            </SidebarHeader>

            <SidebarContent className="gap-2 p-2">
                <SidebarGroup className="pl-0">
                    <SidebarGroupLabel className="-mx-2 group-data-[collapsible=icon]:!mt-0">Core</SidebarGroupLabel>

                    <SidebarMenu className="-mx-2">
                        <SidebarMenuItem>
                            <OrbitSidebarMenuButton
                                tooltip="Find & Fix"
                                isActive={isFindAndFixActive}
                                {...findAndFixButtonProps}
                            >
                                <OrbitSidebarMenuButton.Icon imgSrc="/find-and-fix.svg" />
                                <span className="font-semibold uppercase">
                                    Find &amp; Fix
                                    <span style={{ fontSize: '1em', verticalAlign: 'super', lineHeight: 0 }}>
                                        &reg;
                                    </span>
                                </span>
                            </OrbitSidebarMenuButton>
                        </SidebarMenuItem>

                        <SidebarMenuItem>
                            <OrbitSidebarMenuButton tooltip="SAVI" isActive={isSaviActive} {...saviButtonProps}>
                                <OrbitSidebarMenuButton.Icon imgSrc="/savi-icon.webp" />
                                <span className="font-semibold uppercase">
                                    SAVI
                                    <span style={{ fontSize: '1em', verticalAlign: 'super', lineHeight: 0 }}>
                                        &trade;
                                    </span>
                                </span>
                            </OrbitSidebarMenuButton>
                        </SidebarMenuItem>
                    </SidebarMenu>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter>
                <SidebarGroup>
                    <SidebarGroupLabel className="-mx-2">More</SidebarGroupLabel>

                    <SidebarMenu className="-mx-2 w-[calc(100%+1rem)]">
                        {/* Help is state-driven: opens the Help dialog without changing the URL. */}
                        <SidebarMenuItem>
                            <OrbitSidebarMenuButton tooltip="Help" onClick={onHelpClick}>
                                <OrbitSidebarMenuButton.Icon icon={LifeBuoy} />
                                <span className="font-semibold">Help</span>
                            </OrbitSidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Sign Out is state-driven: opens a confirmation dialog without changing the URL. */}
                        <SidebarMenuItem>
                            <OrbitSidebarMenuButton tooltip="Sign Out" onClick={onSignOutClick}>
                                <OrbitSidebarMenuButton.Icon icon={LogOut} />
                                <span className="font-semibold">Sign Out</span>
                            </OrbitSidebarMenuButton>
                        </SidebarMenuItem>

                        <SidebarMenuItem>
                            <SidebarThemeToggle />
                        </SidebarMenuItem>
                    </SidebarMenu>

                    <UserInfo
                        name={userName ?? 'Unknown User'}
                        email={userEmail ?? ''}
                        className="mt-4"
                        avatarUrl={userAvatarUrl}
                    />
                </SidebarGroup>
            </SidebarFooter>
        </Sidebar>
    );
}
